import useApi from 'common/hooks/useApi';
import useTypedSelector from 'common/hooks/useTypedSelector';
import ActionStatus from 'common/types/ActionStatus';
import FormVerificationResponse from 'domain/components/verify-identity/FormVerificationResponse';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import DraftApplication from '../../../types/DraftApplication';
import ResidenceUpload from './ResidenceUpload';
import upload from './upload';
import { GroupReferenceKey } from 'features/cart/CartSlice';

interface IResidenceUploadContainer {
  showValidations: boolean;
  groupRefKey?: GroupReferenceKey;
}

const ResidenceUploadContainer = (props: IResidenceUploadContainer) => {
  const { groupRefKey, token } = useTypedSelector((state) => ({
    groupRefKey: props.groupRefKey ?? state.draftApplication.data.groupRefKey,
    token: state.auth.access_token,
  }));

  const [{ data: verification }, fetchStatus] =
    useApi<FormVerificationResponse>({
      url: '',
      method: 'GET',
    });

  const refreshVerification = useCallback(() => {
    if (groupRefKey !== null) {
      fetchStatus(
        undefined,
        `/applications/primary-residence-status?groupRefKey=` + groupRefKey
      );
    }
  }, [fetchStatus, groupRefKey]);

  // Get primary residence status on page load
  useEffect(() => {
    refreshVerification();
  }, [refreshVerification]);

  const [document1, setDocument1] = React.useState<File>();
  const [document2, setDocument2] = React.useState<File>();
  const [attachmentType1Cd, setAttachmentType1Cd] = React.useState<
    string | null
  >();
  const [attachmentType2Cd, setAttachmentType2Cd] = React.useState<
    string | null
  >();
  const [{ uploadStatus, uploadMessage }, setUpload] = useState({
    uploadStatus: ActionStatus.none,
    uploadMessage: '',
  });

  const { change } = useForm<DraftApplication>();

  // Anytime file fields change, fire submit
  useEffect(() => {
    async function doUpload() {
      if (
        groupRefKey &&
        token &&
        document1 &&
        document2 &&
        attachmentType1Cd &&
        attachmentType2Cd
      ) {
        if (attachmentType1Cd === attachmentType2Cd) {
          setUpload({
            uploadStatus: ActionStatus.failed,
            uploadMessage:
              'Primary residence documents must have different document types.',
          });
        } else {
          try {
            setUpload({
              uploadStatus: ActionStatus.saving,
              uploadMessage: '',
            });
            await upload(
              groupRefKey,
              token,
              '/attachments/primary-residence',
              true,
              null,
              null,
              undefined,
              undefined,
              [
                {
                  files: document1,
                  attachmentTypeCd: attachmentType1Cd,
                  uploadTypeCd: 'PRIMARY_RESIDENCE',
                },
                {
                  files: document2,
                  attachmentTypeCd: attachmentType2Cd,
                  uploadTypeCd: 'PRIMARY_RESIDENCE_02',
                },
              ]
            );
            setUpload({
              uploadStatus: ActionStatus.saved,
              uploadMessage: 'Primary residence document has been uploaded.',
            });
            refreshVerification();
          } catch (e) {
            setUpload({
              uploadStatus: ActionStatus.failed,
              uploadMessage: (e instanceof Error && e.message.toString()) || '',
            });
          }
        }
      }
    }

    doUpload();
  }, [
    groupRefKey,
    document1,
    document2,
    token,
    attachmentType1Cd,
    attachmentType2Cd,
    refreshVerification,
    change,
  ]);

  const handleFileChange = (
    fileList: FileList | null,
    setState: React.Dispatch<React.SetStateAction<File | undefined>>
  ) => {
    if (!fileList) return;
    setState(fileList[0]);
  };

  const hasAttachment = Boolean(
    verification && verification.primaryResAttachment.length >= 2
  );

  useEffect(() => {
    change('hasResidenceAttachment', hasAttachment);
  }, [hasAttachment, change]);

  return (
    <ResidenceUpload
      showValidations={props.showValidations}
      verification={verification}
      uploadStatus={uploadStatus}
      uploadMessage={uploadMessage}
      onAttachmentType1Change={(value: string) => {
        setAttachmentType1Cd(value);
        change('hasDifferentAttachmentTypes', value !== attachmentType2Cd);
      }}
      onAttachmentType2Change={(value: string) => {
        setAttachmentType2Cd(value);
        change('hasDifferentAttachmentTypes', value !== attachmentType1Cd);
      }}
      onDocument1Change={(e: React.ChangeEvent<HTMLInputElement>) => {
        handleFileChange(e.target.files, setDocument1);
      }}
      onDocument2Change={(e: React.ChangeEvent<HTMLInputElement>) => {
        handleFileChange(e.target.files, setDocument2);
      }}
      attachment1={document1}
      attachment2={document2}
      filesLoaded={document1 !== undefined && document2 !== undefined}
    />
  );
};

export default ResidenceUploadContainer;
